import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBookmarkedMessages } from "../../../core/repo/sessionRepo";
import Page from "../../../components/Page";
import { makeStyles } from "tss-react/mui";
import Loader from "../../../components/Loader";
import { Box, Grid } from "@mui/material";
import Header from "./Header";
import { groupMessagesByQuestion } from "../../AI_Search/AI_Search/utils";
import SourceDialog from "../../AI_Search/AI_Search/components/SourceDialog";
import EntityDialog from "../../AI_Search/AI_Search/components/EntityDialog";
import TabularDialog from "../../AI_Search/AI_Search/components/TabularDialog";
import Results from "../../AI_Search/AI_Search/Results/Results";
import { useLocation } from "react-router-dom";
import NoBookmarks from "./NoBookmarks";
import ScrollToBottom from "../../AI_Search/AI_Search/components/ScrollToBottom";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

function Bookmarks() {
  const location = useLocation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [bookmarkedMessages, setBookmarkedMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const messageRefs = useRef({});
  const messagesRef = useRef();
  const messagesEndRef = useRef();
  const [entity, setEntity] = useState(null);
  const [tabular, setTabular] = useState(null);
  const messageId = new URLSearchParams(location.search).get("messageId");
  const [selectedSourcesMessage, setSelectedSourcesMessage] = useState(null);
  const { selectedDomain } = useSelector((state) => state.domain);

  const fetchBookmarkedMessages = async () => {
    setIsLoading(true);
    try {
      setBookmarkedMessages([]);
      const response = await dispatch(
        getBookmarkedMessages(selectedDomain["id"])
      );
      const listOfQuestionAndAnswerPairs = groupMessagesByQuestion(
        response["messages"] || []
      );
      setBookmarkedMessages(listOfQuestionAndAnswerPairs);
    } catch (err) {
      console.log(err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    selectedDomain && fetchBookmarkedMessages();
  }, [selectedDomain]);

  useEffect(() => {
    if (!isLoading) {
      handleScroll();
    }
  }, [isLoading, bookmarkedMessages.length]);

  const handleScroll = () => {
    setTimeout(() => {
      if (messageId && messageRefs.current[messageId]) {
        messageRefs.current[messageId].scrollIntoView({
          behavior: "smooth",
        });
      } else if (messagesEndRef.current) {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 10);
  };

  const handleCloseSourceDialog = () => {
    setSelectedSourcesMessage(null);
  };

  const handleEntityChange = (entity) => {
    setSelectedSourcesMessage(null); // close source dialog first
    setEntity(entity);
    setTabular(null);
  };

  const handleTabularChange = (tabular) => {
    setSelectedSourcesMessage(null); // close source dialog first
    setTabular(tabular);
    setEntity(null);
  };

  const handleSelectedSourcesMessageChange = (message) => {
    setSelectedSourcesMessage(message);
    setEntity(null); // close entity dialog first
    setTabular(null); // close tabular dialog first
  };

  return (
    <Page className={classes.root} title={"Saved Answers"}>
      <Box
        height={"100%"}
        display={"flex"}
        flexDirection={"column"}
        overflow={"hidden"}>
        <Header />
        <Box
          flex={1}
          overflow={"hidden"}
          display={"flex"}
          flexDirection={"column"}
          position={"relative"}>
          <Grid container height={"100%"}>
            <Grid
              item
              size={{
                md: selectedSourcesMessage ? 7 : entity || tabular ? 4 : 12,
              }}
              height={"100%"}
              width={"100%"}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
                position={"relative"}>
                <Box ref={messagesRef} flex={1} overflow={"auto"}>
                  <Box
                    height={"100%"}
                    p={{ xs: 2, sm: 3 }}
                    maxWidth={900}
                    margin={"0 auto"}>
                    {bookmarkedMessages.length === 0 && !isLoading ? (
                      <NoBookmarks />
                    ) : (
                      <Results
                        messages={bookmarkedMessages}
                        messageRefs={messageRefs}
                        handleEntityChange={handleEntityChange}
                        handleTabularChange={handleTabularChange}
                        showToolbar={false}
                        saved={true}
                        setSelectedSourcesMessage={
                          handleSelectedSourcesMessageChange
                        }
                      />
                    )}
                    <Box ref={messagesEndRef} />
                  </Box>
                </Box>
                <ScrollToBottom
                  messagesRef={messagesRef}
                  messagesEndRef={messagesEndRef}
                />
              </Box>
            </Grid>
            {selectedSourcesMessage && (
              <SourceDialog
                selectedSourcesMessage={selectedSourcesMessage}
                onClose={handleCloseSourceDialog}
              />
            )}
            {entity && (
              <EntityDialog
                entity={entity}
                handleEntityChange={handleEntityChange}
              />
            )}
            {tabular && (
              <TabularDialog
                fileId={tabular["file_id"]}
                source={tabular}
                handleSourceChange={handleTabularChange}
              />
            )}
          </Grid>
          {isLoading && <Loader />}
        </Box>
      </Box>
    </Page>
  );
}

export default Bookmarks;
